import React, { useEffect, useMemo, useState } from "react";
import { parseDate } from "@/lib/dates";
import { useNavigate } from "react-router-dom";
import {
  Plus, Anchor, Calendar, Users as UsersIcon, ExternalLink, Copy, Archive,
  Search, X, Loader2, Wand2,
} from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { confirmAsync } from "@/lib/safeConfirm";
import { TravefyImportModal } from "./TravefyImportModal";

// ---------------------------------------------------------------------------
// Curated Trips dashboard (Fase 4, 2026-09-15)
// ---------------------------------------------------------------------------
// One row per curated trip. Shows dates, capacity utilisation, currency,
// per-person prices, revenue captured, publish status, and a public link
// button. New trips created inline via a minimal modal; the details, days
// and Bookings tab live inside CuratedTripBuilder.

const STATUS_BADGE = {
  draft:      { label: "Borrador",   klass: "bg-clay-200 text-clay-900" },
  published:  { label: "Publicado",  klass: "bg-pine text-white" },
  closed:     { label: "Cerrado",    klass: "bg-clay-500 text-white" },
  archived:   { label: "Archivado",  klass: "bg-clay-400 text-white" },
};

const fmtDate = (d) => {
  if (!d) return "—";
  try { return parseDate(d).toLocaleDateString("es-ES", { day: "2-digit", month: "short", year: "numeric" }); }
  catch { return d; }
};

const fmtMoney = (n, ccy) => {
  const currency = (ccy || "USD").toUpperCase();
  return new Intl.NumberFormat(currency === "EUR" ? "es-ES" : "en-US", {
    style: "currency", currency,
    minimumFractionDigits: 0, maximumFractionDigits: 0,
  }).format(n || 0);
};

// Aggregate revenue captured for a trip: sum of every booking's captured
// payments. Falls back to the booking-level paid_amount for legacy Fase-1
// bookings that don't yet have the payments[] ledger.
function revenueForTrip(trip) {
  let total = 0;
  for (const b of (trip.bookings || [])) {
    const captured = (b.payments || []).filter((p) => p.status === "captured");
    if (captured.length) {
      total += captured.reduce((s, p) => s + (p.amount_eur || 0), 0);
    } else if (b.status === "paid" && b.paid_amount) {
      total += b.paid_amount;
    }
  }
  return total;
}

export default function CuratedTripsList() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [search, setSearch] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [showTravefy, setShowTravefy] = useState(false);
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/curated-trips");
      setItems(data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No pudimos cargar los curated trips");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter((t) =>
      (t.name || "").toLowerCase().includes(q) ||
      (t.tagline || "").toLowerCase().includes(q),
    );
  }, [items, search]);

  const handleCreate = async (payload) => {
    setCreating(true);
    try {
      const { data } = await api.post("/curated-trips", payload);
      toast.success("Curated trip creado");
      setShowCreate(false);
      navigate(`/curated-trips/${data.curated_trip_id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo crear");
    } finally { setCreating(false); }
  };

  const handleArchive = async (trip) => {
    const ok = await confirmAsync({
      title: `Archivar "${trip.name}"`,
      body: "El viaje se ocultará. No se puede archivar si tiene reservas pagadas.",
      confirmText: "Archivar",
    });
    if (!ok) return;
    try {
      await api.delete(`/curated-trips/${trip.curated_trip_id}`);
      toast.success("Archivado");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo archivar");
    }
  };

  const copyPublicLink = async (trip) => {
    const url = `${window.location.origin}/curated/${trip.public_token}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link público copiado");
    } catch {
      // fallback for browsers without clipboard permissions
      window.prompt("Copia el link:", url);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-8 py-10" data-testid="curated-trips-list">
      {/* Header */}
      <div className="flex items-baseline justify-between mb-8">
        <div>
          <div className="smallcaps text-terracotta mb-1">Small-group departures</div>
          <h1 className="font-serif text-4xl text-clay-900 flex items-center gap-3">
            <Anchor size={28}/> Curated Trips
          </h1>
          <p className="mt-2 text-sm text-clay-700 max-w-xl">
            Viajes en grupo pequeño con fechas fijas y cupo limitado. Cada viaje se comparte con un único link público donde el cliente elige plazas y habitación.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowTravefy(true)}
            data-testid="new-curated-from-travefy-btn"
            className="inline-flex items-center gap-2 border border-clay-900 text-clay-900 hover:bg-clay-900 hover:text-white px-4 py-2.5 rounded-md text-sm font-medium"
            title="Importa un itinerario publicado de Travefy como curated trip (borrador)"
          >
            <Wand2 size={14}/> Nuevo desde Travefy
          </button>
          <button
            onClick={() => setShowCreate(true)}
            data-testid="new-curated-trip-btn"
            className="inline-flex items-center gap-2 bg-clay-900 hover:bg-black text-white px-4 py-2.5 rounded-md text-sm font-medium"
          >
            <Plus size={14}/> Nuevo curated trip
          </button>
        </div>
      </div>

      {/* Search */}
      <div className="mb-6 max-w-md relative">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-clay-500"/>
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Buscar por nombre o tagline…"
          data-testid="curated-search"
          className="w-full pl-9 pr-9 py-2 border border-clay-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-terracotta"
        />
        {search && (
          <button onClick={() => setSearch("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-clay-500">
            <X size={12}/>
          </button>
        )}
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex items-center gap-2 text-clay-600">
          <Loader2 size={14} className="animate-spin"/> Cargando curated trips…
        </div>
      ) : filtered.length === 0 ? (
        <EmptyState onCreate={() => setShowCreate(true)}/>
      ) : (
        <div className="border border-clay-300 rounded-md overflow-hidden bg-white">
          <table className="w-full text-sm">
            <thead className="bg-clay-100 border-b border-clay-300">
              <tr className="text-left text-[11px] uppercase tracking-wider text-clay-700">
                <th className="px-4 py-3 font-medium">Nombre</th>
                <th className="px-4 py-3 font-medium">Fechas</th>
                <th className="px-4 py-3 font-medium">Plazas</th>
                <th className="px-4 py-3 font-medium whitespace-nowrap" title="Visitas a la landing pública (últimos 7 / 30 días), sin equipo ni robots">Visitas 7d / 30d</th>
                <th className="px-4 py-3 font-medium">Precios (single / double)</th>
                <th className="px-4 py-3 font-medium">Recaudado</th>
                <th className="px-4 py-3 font-medium">Estado</th>
                <th className="px-4 py-3 font-medium w-32">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((t) => (
                <tr key={t.curated_trip_id} className="border-b border-clay-200 last:border-b-0 hover:bg-clay-50" data-testid={`row-${t.curated_trip_id}`}>
                  <td className="px-4 py-3">
                    <button
                      onClick={() => navigate(`/curated-trips/${t.curated_trip_id}`)}
                      className="text-left font-medium text-clay-900 hover:underline"
                    >
                      {t.name}
                    </button>
                    {t.tagline && (
                      <div className="text-[12px] text-clay-600 truncate max-w-md">{t.tagline}</div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-clay-700 whitespace-nowrap">
                    <div className="inline-flex items-center gap-1.5">
                      <Calendar size={12} className="text-clay-500"/>
                      {fmtDate(t.start_date)} → {fmtDate(t.end_date)}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-clay-700 whitespace-nowrap">
                    <div className="inline-flex items-center gap-1.5">
                      <UsersIcon size={12} className="text-clay-500"/>
                      <span className="tabular">{t.seats_committed}/{t.capacity}</span>
                      {t.seats_available === 0 && (
                        <span className="ml-1 text-[10px] uppercase text-terracotta font-medium">Sold out</span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-clay-700 tabular whitespace-nowrap" data-testid={`views-${t.curated_trip_id}`}>
                    <button onClick={() => navigate(`/curated-trips/${t.curated_trip_id}?tab=stats`)} className="hover:underline" title={`${t.stats?.visitors_30d ?? 0} visitantes únicos en 30 días`}>
                      {t.stats?.views_7d ?? 0} <span className="text-clay-400">/</span> {t.stats?.views_30d ?? 0}
                    </button>
                  </td>
                  <td className="px-4 py-3 text-clay-700 tabular whitespace-nowrap">
                    {fmtMoney(t.price_single, t.currency)} / {fmtMoney(t.price_double, t.currency)}
                  </td>
                  <td className="px-4 py-3 text-clay-900 tabular font-medium whitespace-nowrap" data-testid={`revenue-${t.curated_trip_id}`}>
                    {fmtMoney(revenueForTrip(t), t.currency)}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium ${STATUS_BADGE[t.status]?.klass || ""}`}>
                      {STATUS_BADGE[t.status]?.label || t.status}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1">
                      {t.status === "published" && (
                        <button
                          onClick={() => copyPublicLink(t)}
                          className="p-1.5 hover:bg-clay-200 rounded text-clay-700"
                          title="Copiar link público"
                          data-testid={`copy-link-${t.curated_trip_id}`}
                        >
                          <Copy size={14}/>
                        </button>
                      )}
                      {t.status === "published" && (
                        <a
                          href={`/curated/${t.public_token}`}
                          target="_blank" rel="noopener noreferrer"
                          className="p-1.5 hover:bg-clay-200 rounded text-clay-700"
                          title="Ver landing pública"
                          data-testid={`view-public-${t.curated_trip_id}`}
                        >
                          <ExternalLink size={14}/>
                        </a>
                      )}
                      <button
                        onClick={() => handleArchive(t)}
                        className="p-1.5 hover:bg-clay-200 rounded text-clay-700"
                        title="Archivar"
                        data-testid={`archive-${t.curated_trip_id}`}
                      >
                        <Archive size={14}/>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showCreate && (
        <CreateCuratedTripModal
          onCancel={() => setShowCreate(false)}
          onConfirm={handleCreate}
          submitting={creating}
        />
      )}

      {showTravefy && (
        <TravefyImportModal
          mode="curated"
          onClose={() => { setShowTravefy(false); load(); }}
        />
      )}
    </div>
  );
}

function EmptyState({ onCreate }) {
  return (
    <div className="border border-dashed border-clay-300 rounded-md p-16 text-center bg-clay-50/60">
      <Anchor size={32} className="mx-auto text-clay-500 mb-4"/>
      <h3 className="font-serif text-2xl text-clay-900">Aún no tienes ningún curated trip</h3>
      <p className="mt-2 text-sm text-clay-700 max-w-md mx-auto">
        Crea tu primer viaje en grupo pequeño para empezar a vender plazas con fecha fija y precio por persona.
      </p>
      <button
        onClick={onCreate}
        className="mt-6 inline-flex items-center gap-2 bg-clay-900 hover:bg-black text-white px-4 py-2.5 rounded-md text-sm font-medium"
        data-testid="empty-create-btn"
      >
        <Plus size={14}/> Crear el primero
      </button>
    </div>
  );
}

function CreateCuratedTripModal({ onCancel, onConfirm, submitting }) {
  const [name, setName] = useState("");
  const [capacity, setCapacity] = useState(12);
  const [priceSingle, setPriceSingle] = useState(0);
  const [priceDouble, setPriceDouble] = useState(0);
  const [currency, setCurrency] = useState("USD");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const canSubmit = name.trim().length >= 2 && capacity > 0;

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!canSubmit || submitting) return;
    onConfirm({
      name: name.trim(),
      capacity: Number(capacity),
      price_single: Number(priceSingle) || 0,
      price_double: Number(priceDouble) || 0,
      currency,
      start_date: startDate || null,
      end_date: endDate || null,
    });
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-clay-900/40 flex items-center justify-center p-6"
      onClick={onCancel}
      data-testid="create-curated-modal"
    >
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={handleSubmit}
        className="bg-white rounded-md shadow-xl w-full max-w-lg"
      >
        <div className="px-6 py-4 border-b border-clay-200">
          <h2 className="font-serif text-2xl">Nuevo curated trip</h2>
          <p className="text-[13px] text-clay-600 mt-1">
            Solo lo esencial — el resto (imagen, descripción, itinerario, políticas) se rellena en el builder.
          </p>
        </div>
        <div className="px-6 py-5 space-y-4">
          <label className="block">
            <span className="smallcaps text-clay-700">Nombre del viaje *</span>
            <input
              type="text" value={name} onChange={(e) => setName(e.target.value)}
              data-testid="new-curated-name" autoFocus required
              placeholder="e.g. Piedmont & Lakes — October 2026"
              className="mt-1 w-full border border-clay-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-terracotta"
            />
          </label>
          <div className="grid grid-cols-2 gap-4">
            <label className="block">
              <span className="smallcaps text-clay-700">Fecha inicio</span>
              <input
                type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)}
                data-testid="new-curated-start"
                className="mt-1 w-full border border-clay-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-terracotta"
              />
            </label>
            <label className="block">
              <span className="smallcaps text-clay-700">Fecha fin</span>
              <input
                type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)}
                data-testid="new-curated-end"
                className="mt-1 w-full border border-clay-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-terracotta"
              />
            </label>
          </div>
          <div className="grid grid-cols-3 gap-4">
            <label className="block col-span-1">
              <span className="smallcaps text-clay-700">Cupo</span>
              <input
                type="number" min="1" max="100" value={capacity}
                onChange={(e) => setCapacity(e.target.value)}
                data-testid="new-curated-capacity"
                className="mt-1 w-full border border-clay-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-terracotta"
              />
            </label>
            <label className="block col-span-1">
              <span className="smallcaps text-clay-700">Moneda</span>
              <select
                value={currency} onChange={(e) => setCurrency(e.target.value)}
                data-testid="new-curated-currency"
                className="mt-1 w-full border border-clay-300 rounded px-3 py-2 text-sm bg-white"
              >
                <option value="USD">USD ($)</option>
                <option value="EUR">EUR (€)</option>
              </select>
            </label>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <label className="block">
              <span className="smallcaps text-clay-700">Precio hab. individual / persona</span>
              <input
                type="number" min="0" value={priceSingle}
                onChange={(e) => setPriceSingle(e.target.value)}
                data-testid="new-curated-price-single"
                className="mt-1 w-full border border-clay-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-terracotta"
              />
            </label>
            <label className="block">
              <span className="smallcaps text-clay-700">Precio hab. doble / persona</span>
              <input
                type="number" min="0" value={priceDouble}
                onChange={(e) => setPriceDouble(e.target.value)}
                data-testid="new-curated-price-double"
                className="mt-1 w-full border border-clay-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-terracotta"
              />
            </label>
          </div>
        </div>
        <div className="px-6 py-4 border-t border-clay-200 flex items-center justify-end gap-2">
          <button type="button" onClick={onCancel} className="text-sm text-clay-700 hover:text-clay-900 px-3 py-2">
            Cancelar
          </button>
          <button
            type="submit"
            disabled={!canSubmit || submitting}
            data-testid="new-curated-submit"
            className="inline-flex items-center gap-2 bg-clay-900 hover:bg-black disabled:bg-clay-400 text-white px-4 py-2.5 rounded-md text-sm font-medium"
          >
            {submitting ? <><Loader2 size={14} className="animate-spin"/> Creando…</> : <>Crear y abrir builder</>}
          </button>
        </div>
      </form>
    </div>
  );
}
